import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, MapPin, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher, useCategories } from "@/lib/hooks";
import { StatusBadge, AuthImage } from "@/components/common";
import { MetersList } from "@/components/Meters";
import { UnitDialog } from "@/components/UnitDialog";
import { LeaseDialog } from "@/components/LeaseDialog";

function Info({ label, children }) {
  return <div><div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div><div className="mt-1 text-sm text-slate-900">{children}</div></div>;
}

function UnitRow({ u, staff, onDelete }) {
  const { label } = useCategories();
  const { t } = usePrefs();
  return (
    <tr className="border-b border-slate-100 text-sm hover:bg-slate-50" data-testid={`unit-row-${u.id}`}>
      <td className="py-3 pl-4 font-data text-xs">{u.reference}</td>
      <td>{u.is_common ? u.type : label(u.type)}</td>
      <td className="text-slate-500">{[u.building, u.floor].filter(Boolean).join(" · ") || "—"}</td>
      <td className="font-data text-xs">{u.surface ? `${u.surface} m²` : "—"}</td>
      <td><MetersList meters={u.meters} /></td>
      <td><StatusBadge value={u.status} /></td>
      <td className="pr-4 text-right">
        {staff && u.status === "vacant" && <LeaseDialog unit={u} trigger={<Button size="sm" variant="outline" className="mr-2 h-7 rounded-sm text-xs" data-testid={`new-lease-${u.id}`}>{t("newLease")}</Button>} />}
        {staff && u.status !== "occupe" && <button onClick={() => onDelete(u.id)} className="text-slate-400 hover:text-rose-600" data-testid={`delete-unit-${u.id}`}><Trash2 size={14} /></button>}
      </td>
    </tr>
  );
}

function UnitsTable({ units, staff, onDelete, title }) {
  const { t } = usePrefs();
  return (
    <div className="overflow-x-auto rounded-sm border border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-4 py-3 font-display text-lg font-bold">{title} <span className="font-data text-xs text-slate-400">({units.length})</span></div>
      <table className="w-full min-w-[720px]">
        <thead><tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
          <th className="py-2 pl-4">{t("reference")}</th><th>{t("type")}</th><th>{t("building")} / {t("floor")}</th><th>{t("surface")}</th><th>{t("meters")}</th><th>{t("status")}</th><th />
        </tr></thead>
        <tbody>{units.map((u) => <UnitRow key={u.id} u={u} staff={staff} onDelete={onDelete} />)}</tbody>
      </table>
    </div>
  );
}

export default function PropertyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = usePrefs();
  const { label } = useCategories();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: p } = useQuery({ queryKey: ["property", id], queryFn: () => fetcher(`/properties/${id}`) });
  const staff = ["admin", "supervision", "gestionnaire"].includes(user.role);
  if (!p) return <div className="h-64 animate-pulse bg-slate-200/60" />;
  const delUnit = async (uid) => {
    try { await api.delete(`/units/${uid}`); qc.invalidateQueries({ queryKey: ["property", id] }); } catch (e) { toast.error(errMsg(e)); }
  };
  const delProp = async () => {
    if (!window.confirm("Supprimer ce bien ?")) return;
    try { await api.delete(`/properties/${id}`); qc.invalidateQueries({ queryKey: ["properties"] }); nav("/properties"); } catch (e) { toast.error(errMsg(e)); }
  };
  const lots = p.units.filter((u) => !u.is_common);
  const commons = p.units.filter((u) => u.is_common);
  return (
    <div data-testid="property-detail-page">
      <Link to="/properties" className="mb-4 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"><ArrowLeft size={13} />{t("properties")}</Link>
      <div className="rise grid overflow-hidden rounded-sm border border-slate-200 bg-white lg:grid-cols-[1.1fr_1fr]">
        <div className="h-64 lg:h-auto"><AuthImage path={p.photos?.[0] || "https://images.unsplash.com/photo-1719258833451-1e47c1498467?w=1200&q=80"} className="h-full w-full object-cover" /></div>
        <div className="p-7">
          <div className="eyebrow">{label(p.type)} · <span className="font-data">{p.reference}</span></div>
          <h1 className="mt-2 font-display text-4xl font-bold" data-testid="property-title">{p.name}</h1>
          <div className="mt-2 flex items-center gap-1 text-sm text-slate-500"><MapPin size={14} />{p.address}, {p.city}</div>
          {p.description && <p className="mt-4 text-sm text-slate-600">{p.description}</p>}
          <div className="mt-6 grid grid-cols-2 gap-5">
            <Info label={t("owners")}>{p.owners.map((o) => o.name).join(", ") || "—"}</Info>
            <Info label={t("manager")}>{p.manager?.name || "—"}</Info>
            <Info label={t("surface")}><span className="font-data">{p.surface ?? "—"}</span></Info>
            <Info label={t("rooms")}><span className="font-data">{p.rooms ?? "—"}</span></Info>
            <Info label={t("equipment")}>{p.equipment?.join(" · ") || "—"}</Info>
            <Info label={t("meters")}><MetersList meters={p.meters} /></Info>
          </div>
          {p.photos?.length > 1 && <div className="mt-5 flex gap-2">{p.photos.slice(1).map((ph) => <AuthImage key={ph} path={ph} className="h-14 w-20 rounded-sm object-cover" />)}</div>}
          {staff && <button onClick={delProp} className="mt-6 text-xs text-slate-400 hover:text-rose-600" data-testid="delete-property-button">Supprimer le bien</button>}
        </div>
      </div>
      <div className="mt-8 flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold">{t("units")}</h2>
        {staff && <UnitDialog propertyId={id} ownerIds={p.owner_ids} trigger={<Button className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="new-unit-button"><Plus size={16} className="mr-1" />{t("newUnit")}</Button>} />}
      </div>
      <div className="mt-4 space-y-6">
        <UnitsTable units={lots} staff={staff} onDelete={delUnit} title={t("units")} />
        {commons.length > 0 && <UnitsTable units={commons} staff={staff} onDelete={delUnit} title={t("commonAreas")} />}
      </div>
    </div>
  );
}
